import {
  Badge,
  Link,
} from '@chakra-ui/react';
import { Edit, Trash2, ExternalLink, Pill } from 'lucide-react';
import { TableColumn, TableAction, ActionHandler } from '@/components/section/EntityTableSection';
import { ConditionResourceLookup } from '../api';
import RelativeTime from '@/components/ui/RelativeTime';
import { ConditionResourceAction } from '../hooks/useConditionResourceActions';
import { getStatusBadgeProps } from '@/utils/lookupStatus';
import { CONDITION_RESOURCES_FEATURES } from '../constants';

export const createPrescriptionsColumns = (): TableColumn<ConditionResourceLookup>[] => [
  {
    key: "id",
    title: "ID",
    variant: "id",
  },
  {
    key: "condition_lookup_id",
    title: "CONDITION",
    render: (resource) => (
      <Link
        href={`/conditions/#/${resource.condition_lookup_id}/view`}
        color="brand.500"
        fontSize="sm"
        fontWeight="medium"
      >
        #{resource.condition_lookup_id}
      </Link>
    ),
  },
  {
    key: "title",
    title: "TITLE",
    variant: "primary",
  },
  {
    key: "url",
    title: "LINK",
    render: (resource) => (
      <Link
        href={resource.url}
        target="_blank"
        color="brand.500"
        fontSize="sm"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <Pill size={14} />
        View
        <ExternalLink size={12} />
      </Link>
    ),
  },
  ...(CONDITION_RESOURCES_FEATURES.SHOW_STATUS_COLUMN ? [{
    key: "status" as const,
    title: "STATUS",
    render: (resource: ConditionResourceLookup) => {
      const { colorPalette, label } = getStatusBadgeProps(resource.status);
      return <Badge colorPalette={colorPalette} size="sm">{label}</Badge>;
    },
  }] : []),
  {
    key: "is_valid",
    title: "VALID",
    render: (resource) => {
      if (resource.is_valid === true) {
        return <Badge colorPalette="green" size="sm">Valid</Badge>;
      } else if (resource.is_valid === false) {
        return <Badge colorPalette="red" size="sm">Invalid</Badge>;
      } else {
        return <Badge colorPalette="gray" size="sm">Unknown</Badge>;
      }
    },
  },
  {
    key: "created",
    title: "ADDED",
    render: (resource) => (
      <RelativeTime date={resource.created_at || new Date().toISOString()} />
    ),
  },
];

export const createPrescriptionsActions = (
  onAction: ActionHandler<ConditionResourceLookup, ConditionResourceAction>,
  isLoading: boolean
): TableAction<ConditionResourceLookup>[] => [
  {
    key: "edit",
    label: "Edit",
    icon: Edit,
    onClick: (entity) => onAction("edit", entity),
    disabled: isLoading,
  },
  {
    key: "delete",
    label: "Delete",
    icon: Trash2,
    onClick: (entity) => onAction("delete", entity),
    disabled: isLoading,
    colorPalette: "red",
  },
];
